import React, { useEffect, useRef, useState } from 'react';
import { documentsApi } from '../services/backendApi';
import { useDocuments } from '../hooks/useDocuments';
import { ModalPortal } from './ui';
import type { Document, DocumentType } from '../types';

const MAX_FILE_SIZE = 10 * 1024 * 1024;
const ALLOWED_TYPES = ['application/pdf', 'image/jpeg', 'image/png', 'image/webp'];

interface Props {
  entityId: string;
  entityType: 'qualification' | 'reference';
  defaultDocumentType?: DocumentType;
}

const isPdf = (doc: Document) =>
  doc.mime_type === 'application/pdf' || doc.file_name.endsWith('.pdf');

export const LinkedDocuments: React.FC<Props> = ({ entityId, entityType, defaultDocumentType = 'certificate' }) => {
  const { uploadDocument } = useDocuments();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [docs, setDocs] = useState<Document[]>([]);
  const [loadingDocs, setLoadingDocs] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [fileError, setFileError] = useState<string | null>(null);
  const [viewerDoc, setViewerDoc] = useState<Document | null>(null);

  const fetchDocs = async () => {
    setLoadingDocs(true);
    try {
      const all = await documentsApi.getAll();
      const filtered = all.filter(d =>
        entityType === 'qualification'
          ? d.qualification_id === entityId
          : d.reference_id === entityId
      );
      setDocs(filtered);
    } catch {
      // silently fail — docs are optional supporting material
    } finally {
      setLoadingDocs(false);
    }
  };

  useEffect(() => {
    fetchDocs();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entityId, entityType]);

  const handleFile = async (file: File) => {
    setFileError(null);
    if (file.size > MAX_FILE_SIZE) {
      setFileError('File must be under 10MB');
      return;
    }
    if (!ALLOWED_TYPES.includes(file.type) && !file.name.match(/\.(pdf|jpg|jpeg|png|webp)$/i)) {
      setFileError('Allowed: PDF, JPG, PNG, WEBP');
      return;
    }

    setUploading(true);
    const options =
      entityType === 'qualification'
        ? { qualificationId: entityId }
        : { referenceId: entityId };

    const result = await uploadDocument(file, defaultDocumentType, options);
    if (result.success) {
      await fetchDocs();
    } else {
      setFileError(result.error || 'Upload failed');
    }
    setUploading(false);
  };

  return (
    <div className="mt-3 pt-3 border-t border-gray-100">
      {viewerDoc && (
        <ModalPortal>
        <div
          className="fixed inset-0 bg-black bg-opacity-75 z-[80] flex items-center justify-center p-4"
          onClick={() => setViewerDoc(null)}
        >
          <div
            className="bg-white rounded-xl max-w-4xl w-full max-h-[90vh] flex flex-col"
            onClick={e => e.stopPropagation()}
          >
            <div className="flex items-center justify-between p-4 border-b border-gray-200">
              <p className="font-medium text-gray-900 truncate">{viewerDoc.file_name}</p>
              <div className="flex items-center gap-3">
                <a
                  href={viewerDoc.file_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm text-blue-600 hover:text-blue-700"
                >
                  Open in new tab
                </a>
                <button onClick={() => setViewerDoc(null)} className="p-1.5 rounded hover:bg-gray-100 text-gray-500">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>
            <div className="flex-1 overflow-auto p-4 flex items-center justify-center bg-gray-50 min-h-64">
              {viewerDoc.mime_type?.startsWith('image/') ? (
                <img
                  src={viewerDoc.file_url}
                  alt={viewerDoc.file_name}
                  className="max-w-full max-h-[70vh] object-contain rounded-lg shadow"
                />
              ) : isPdf(viewerDoc) ? (
                <iframe
                  src={viewerDoc.file_url}
                  className="w-full h-[70vh] rounded border"
                  title={viewerDoc.file_name}
                />
              ) : (
                <a
                  href={viewerDoc.file_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 bg-brand-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-brand-700"
                >
                  Download File
                </a>
              )}
            </div>
          </div>
        </div>
        </ModalPortal>
      )}

      <div className="flex items-center justify-between mb-2">
        <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Supporting Documents</p>
        <button
          type="button"
          onClick={() => fileInputRef.current?.click()}
          disabled={uploading}
          className="inline-flex items-center gap-1 text-xs text-blue-600 hover:text-blue-700 font-medium disabled:opacity-50"
        >
          {uploading ? (
            <svg className="w-3.5 h-3.5 animate-spin" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
            </svg>
          ) : (
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
          )}
          {uploading ? 'Uploading…' : 'Attach'}
        </button>
        <input
          ref={fileInputRef}
          type="file"
          accept=".pdf,.jpg,.jpeg,.png,.webp"
          className="hidden"
          onChange={e => { const f = e.target.files?.[0]; if (f) handleFile(f); e.target.value = ''; }}
        />
      </div>

      {fileError && (
        <p className="text-xs text-red-600 mb-2">{fileError}</p>
      )}

      {loadingDocs ? (
        <p className="text-xs text-gray-400">Loading…</p>
      ) : docs.length > 0 ? (
        <div className="space-y-1.5">
          {docs.map(doc => (
            <div key={doc.id} className="flex items-center gap-2 text-xs">
              <svg
                className={`w-3.5 h-3.5 flex-shrink-0 ${isPdf(doc) ? 'text-red-400' : 'text-blue-400'}`}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
              </svg>
              <button
                type="button"
                onClick={() => setViewerDoc(doc)}
                className="text-gray-700 hover:text-blue-600 truncate text-left"
              >
                {doc.file_name}
              </button>
            </div>
          ))}
        </div>
      ) : (
        <p className="text-xs text-gray-400">No documents attached</p>
      )}
    </div>
  );
};
